import { Bars3Icon, ChevronDownIcon, PlusIcon } from '@heroicons/react/24/outline';
import { ExclamationCircleIcon } from '@heroicons/react/16/solid';
import { Draggable, DraggableProvidedDragHandleProps, Droppable } from '@hello-pangea/dnd';
import clsx from 'clsx';
import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Chip, Input, RowAction, RowActions } from '../../../components/ui';
import { PREVIEW_LANGUAGES } from '../../../constants/languages';
import type { ItemRecord, RestaurantInfo } from '../../MenuItems/types';
import { pickName } from '../../MenuItems/utils';
import type { EditorCategory } from '../types';
import { ItemRow } from './ItemRow';

interface Props {
    category: EditorCategory;
    index: number;
    total: number;
    lang: string;
    readOnly: boolean;
    /** An item from this category is being dragged: it can only be dropped in another one. */
    dropBlocked: boolean;
    catalogue: ReadonlyMap<string, ItemRecord>;
    restaurant: RestaurantInfo | null;
    dragHandle: DraggableProvidedDragHandleProps | null;
    dragging: boolean;
    error?: string;
    autoFocus: boolean;
    onRename: (value: string) => void;
    onBlurName: () => void;
    onMove: (delta: -1 | 1) => void;
    onDelete: () => void;
    onAddItems: () => void;
    onRemoveItem: (itemId: string) => void;
    onToggleAvailable: (item: ItemRecord, name: string, next: boolean) => void;
}

export const CategoryCard = ({
    category,
    index,
    total,
    lang,
    readOnly,
    dropBlocked,
    catalogue,
    restaurant,
    dragHandle,
    dragging,
    error,
    autoFocus,
    onRename,
    onBlurName,
    onMove,
    onDelete,
    onAddItems,
    onRemoveItem,
    onToggleAvailable,
}: Props) => {
    const { t } = useTranslation('menu');
    const [collapsed, setCollapsed] = useState(false);
    const input = useRef<HTMLInputElement>(null);
    const listId = useId();
    const errorId = useId();
    const name = category.names[lang] ?? '';
    const label = name || t('editor.untitledCategory');
    const languageName = PREVIEW_LANGUAGES.find((l) => l.code === lang)?.originalLabel ?? lang;

    useEffect(() => {
        if (autoFocus) input.current?.focus();
    }, [autoFocus]);

    const actions: RowAction[] = [
        {
            id: 'rename',
            label: t('editor.rename'),
            // The menu returns focus to its button when it closes; focus the field after that.
            onSelect: () => window.setTimeout(() => input.current?.focus(), 0),
        },
        ...(index > 0 ? [{ id: 'up', label: t('editor.moveUp'), onSelect: () => onMove(-1) }] : []),
        ...(index < total - 1 ? [{ id: 'down', label: t('editor.moveDown'), onSelect: () => onMove(1) }] : []),
        {
            id: 'delete',
            label: t('editor.deleteCategory'),
            onSelect: onDelete,
            tone: 'danger',
            separated: true,
        },
    ];

    return (
        <Card padding="none" className={clsx(dragging && 'scale-[1.02] shadow-e2')}>
            <div className="grid grid-cols-[auto_minmax(0,1fr)_auto_auto] items-center gap-x-1 gap-y-0 px-2 py-1.5 sm:grid-cols-[auto_minmax(0,1fr)_auto_auto_auto]">
                {!readOnly && (
                    <button
                        type="button"
                        {...dragHandle}
                        aria-label={t('editor.reorderCategory', { name: label })}
                        className="col-start-1 row-start-1 flex size-11 shrink-0 cursor-grab items-center justify-center rounded-control text-muted hover:bg-hover"
                    >
                        <Bars3Icon className="size-5" aria-hidden="true" />
                    </button>
                )}
                <Input
                    ref={input}
                    value={name}
                    onChange={(e) => onRename(e.target.value)}
                    onBlur={onBlurName}
                    disabled={readOnly}
                    lang={lang}
                    aria-label={t('editor.categoryName', { language: languageName })}
                    aria-invalid={error ? true : undefined}
                    aria-describedby={error ? errorId : undefined}
                    placeholder={t('editor.categoryPlaceholder')}
                    className="col-start-2 row-start-1 min-w-0 font-semibold"
                />
                <Chip className="col-start-2 row-start-2 mt-1 justify-self-start sm:col-start-3 sm:row-start-1 sm:mx-2 sm:mt-0">
                    {t('editor.itemCount', { count: category.itemIds.length })}
                </Chip>
                <button
                    type="button"
                    onClick={() => setCollapsed((c) => !c)}
                    aria-expanded={!collapsed}
                    aria-controls={listId}
                    aria-label={t(collapsed ? 'editor.expand' : 'editor.collapse', {
                        name: label,
                    })}
                    className="col-start-3 row-start-1 flex size-11 shrink-0 items-center justify-center rounded-control text-strong hover:bg-hover sm:col-start-4"
                >
                    <ChevronDownIcon className={clsx('size-5 transition-transform duration-hover', collapsed && '-rotate-90')} aria-hidden="true" />
                </button>
                {!readOnly && (
                    <div className="col-start-4 row-start-1 sm:col-start-5">
                        <RowActions actions={actions} label={label} />
                    </div>
                )}
            </div>
            {error && (
                <p id={errorId} className="flex items-start gap-1.5 px-4 pb-2 text-caption text-danger">
                    <ExclamationCircleIcon className="mt-px size-4 shrink-0" aria-hidden="true" />
                    {error}
                </p>
            )}
            <div id={listId} hidden={collapsed} className="px-2 pb-3">
                {!collapsed && (
                    <Droppable droppableId={`items:${category.key}`} type="ITEM" isDropDisabled={readOnly || dropBlocked}>
                        {(drop, snapshot) => (
                            <ul
                                ref={drop.innerRef}
                                {...drop.droppableProps}
                                className={clsx('flex min-h-4 flex-col rounded-control', snapshot.isDraggingOver && 'bg-hover')}
                            >
                                {category.itemIds.map((id, i) => {
                                    const item = catalogue.get(id);
                                    if (!item) return null;
                                    const itemName = pickName(item.translations, lang) || t('items.untitled');
                                    return (
                                        <Draggable
                                            key={id}
                                            draggableId={`${category.key}:${id}`}
                                            index={i}
                                            isDragDisabled={readOnly}
                                            disableInteractiveElementBlocking
                                        >
                                            {(drag, dragSnapshot) => (
                                                <li ref={drag.innerRef} {...drag.draggableProps} className="border-t border-hairline first:border-t-0">
                                                    <ItemRow
                                                        item={item}
                                                        name={itemName}
                                                        restaurant={restaurant}
                                                        dragHandle={readOnly || total < 2 ? null : drag.dragHandleProps}
                                                        dragging={dragSnapshot.isDragging}
                                                        editTo={readOnly ? undefined : `/menu-items/edit/${id}`}
                                                        onToggleAvailable={(next) => onToggleAvailable(item, itemName, next)}
                                                        onRemove={readOnly ? undefined : () => onRemoveItem(id)}
                                                    />
                                                </li>
                                            )}
                                        </Draggable>
                                    );
                                })}
                                {drop.placeholder}
                            </ul>
                        )}
                    </Droppable>
                )}
                {category.itemIds.length === 0 && <p className="px-2 py-2 text-small text-muted">{t('editor.noItemsInCategory')}</p>}
                {!readOnly && (
                    <div className="mt-2 px-2">
                        <Button variant="outline" size="sm" icon={<PlusIcon className="size-4" aria-hidden="true" />} onClick={onAddItems}>
                            {t('editor.addItems')}
                        </Button>
                    </div>
                )}
            </div>
        </Card>
    );
};
